import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { HttpErrorResponse } from '@angular/common/http';

import { MessageService } from 'primeng/api';
import { BehaviorSubject, Observable, Subject, Subscription, debounceTime, of, switchMap, take } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { CurationAgentsApiService } from '../../../api/agents/curation-agents.api.service';
import { CuratorQuotaResponse } from '../../../api/agents/models/curation-agent.api';
import { MentionsSearchResponseModel } from '../../../api/mentions/model/mentions-search-response.model';
import { MentionsApi } from '../../../api/mentions/mentions.api';
import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import { DropdownOptionModel } from '../../../Common/Forms/models/dropdown-option.model';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { TakeOverPanelComponent } from '../../../Common/TakeOver/components/TakeOverPanel/take-over-panel.component';
import { AbstractDialogComponent } from '../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { FullProfileAnimationModel } from '../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../Core/Dialogs/enums/overlay-animation.enum';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { MonitoringSeriesTypeEnum } from '../../../Monitor/Shared/enums/monitoring-series-type.enum';
import { MentionSearchCriteriaModel } from '../../../Monitor/Shared/models/mention-search-criteria.model';
import { MonitoringBackEndDataSourceModel } from '../../../Monitor/Shared/models/monitoring-back-end-data-source.model';
import { getAllDateQueryOptions } from '../../../Monitor/Shared/models/monitoring-date-query-input-options';
import { CurationAgentModel } from '../../models/curation-agent.model';
import {
    CurationAgentSourceModel,
    quotaProjectionDays,
    quotaSampleDays
} from '../../models/curation-agent-source.model';
import { CurationAgentsService } from '../../services/curation-agents.service';
import { CurationAgentPanelComponent } from '../CurationAgentPanel/curation-agent-panel.component';

/** Agent types that can be run ad hoc. Only curation exists today */
export enum AdHocAgentTypeEnum {
    curation = 'CURATION'
}

/**
 * What the panel is here to do. The two differ in what they ask for - a live agent watches a source
 * from now on, so it has no date range - and in what they estimate, but the set up is the same.
 */
export enum AgentSourcePanelModeEnum {
    /** Runs the agent once over coverage picked by hand */
    adHoc = 'AD_HOC',
    /** Puts the agent on a source for good */
    liveAgent = 'LIVE_AGENT'
}

/**
 * Puts an agent on a source: either once, over coverage picked by hand ({@link AgentSourcePanelModeEnum.adHoc}),
 * or from now on ({@link AgentSourcePanelModeEnum.liveAgent}). The coverage is picked with the same
 * source picker the curation panel uses.
 */
@Component({
    selector: 'agpr-agent-source-panel',
    templateUrl: './agent-source-panel.component.html',
    styleUrls: ['./agent-source-panel.component.scss'],
    standalone: false
})
export class AgentSourcePanelComponent extends AbstractDialogComponent implements OnInit, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverity = messageSeverityEnum;
    /** The shared chart palette, repeated here for the tooltip the directive renders as raw markup */
    readonly quotaColors = {
        used: '#3459d4',
        estimate: '#0ccbf9',
        overall: '#ad25f0',
        track: '#ced4da'
    };
    readonly dateRangeOptions: DropdownOptionModel[] = getAllDateQueryOptions();

    readonly agentTypeOptions: DropdownOptionModel[] = [
        new DropdownOptionModel('AutomationAgents.adHoc.type.curation', AdHocAgentTypeEnum.curation)
    ];

    readonly panelModes = AgentSourcePanelModeEnum;
    /** Sentinel value for the "Create New Agent" entry in the agent dropdown */
    private readonly newAgentOptionValue: number = -1;

    private readonly dayMs: number = 24 * 60 * 60 * 1000;
    /** What the service answers with when the account has no quota left to spend on a run */
    private readonly conflictStatus: number = 409;

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    /** The agent the panel opens on, when it was launched from one */
    @Input() agent: CurationAgentModel;
    /** Whether the agent is being put on this source for one run or for good */
    @Input() mode: AgentSourcePanelModeEnum = AgentSourcePanelModeEnum.adHoc;
    /** The source the panel was launched from, pre-selected in the picker */
    @Input() sourceType: MonitoringSeriesTypeEnum;
    @Input() sourceId: number;
    /**
     * Filters the source was being viewed with, pre-filled in the picker so they are saved with the
     * agent the way filters picked in the panel are
     */
    @Input() filterSearchCriteria: MentionSearchCriteriaModel;

    @ViewChild('takeOverPanel') takeOverPanel: TakeOverPanelComponent;

    hideSlideOverlay: boolean = false;

    /** The coverage the run will cover, held in the same model the curation panel's rows use */
    readonly source: CurationAgentSourceModel = new CurationAgentSourceModel();
    dateRange: DateQueryModel = DateQueryModel.getLast24HoursQuery();
    agentType: AdHocAgentTypeEnum = AdHocAgentTypeEnum.curation;
    selectedAgentId: number = null;

    agentOptions: DropdownOptionModel[] = [];

    /** Mentions the chosen coverage covers, which is what the run will be charged for */
    mentionCount: number = null;

    /** The account's pool, so the run can be shown against what is left of it */
    private quota: CuratorQuotaResponse;
    countingMentions: boolean = false;
    running: boolean = false;

    /** The agents the dropdown offers, kept so one the create panel adds can be picked out */
    private agents: CurationAgentModel[] = [];
    /** Agent ids as of opening the create panel, held once it saves so the new agent can be selected */
    private agentIdsBeforeCreate: Set<number> = null;

    // The picker re-emits on every filter keystroke, so coalesce before hitting the search endpoint
    private readonly countRequests: Subject<void> = new Subject<void>();
    private readonly subscriptions: Subscription[] = [];

    constructor(
        coreDialogService: CoreDialogService,
        private curationAgentsService: CurationAgentsService,
        private curationAgentsApi: CurationAgentsApiService,
        private mentionsApi: MentionsApi,
        private messageService: MessageService,
        private localizationService: LocalizationService
    ) {
        super(coreDialogService, 'AgentSourcePanel');
    }

    ngOnInit(): void {
        this.selectedAgentId = this.agent?.id ?? null;

        if (this.sourceType && this.sourceId) {
            this.source.dataSource = new MonitoringBackEndDataSourceModel(this.sourceType, Number(this.sourceId));

            if (this.filterSearchCriteria) {
                const filters = this.filterSearchCriteria.clone();

                // The page's range is not the agent's - the picker keeps its own default per type
                filters.query.dateQuery = this.source.dataSource.filterSearchCriteria.query.dateQuery;
                this.source.dataSource.filterSearchCriteria = filters;
            }
        }

        this.subscriptions.push(
            this.curationAgentsService.quota$.subscribe(quota => this.quota = quota),

            this.curationAgentsService.agents$.subscribe(agents => {
                this.agents = agents ?? [];
                this.setAgentOptions();
                this.selectCreatedAgent();
            }),

            this.countRequests.pipe(
                debounceTime(500),
                switchMap(() => this.countMentions())
            ).subscribe(count => {
                this.mentionCount = count;
                this.countingMentions = false;
            })
        );
    }

    get isLiveAgent(): boolean {
        return this.mode === AgentSourcePanelModeEnum.liveAgent;
    }

    get heading(): string {
        return this.isLiveAgent ? 'AutomationAgents.liveAgent.heading' : 'AutomationAgents.adHoc.heading';
    }

    get submitLabel(): string {
        return this.isLiveAgent ? 'AutomationAgents.liveAgent.start' : 'AutomationAgents.adHoc.run';
    }

    get quotaHeading(): string {
        return this.isLiveAgent ? 'AutomationAgents.liveAgent.quotaUsage' : 'AutomationAgents.adHoc.quotaUsage';
    }

    /** What the figure on the estimate card is counting */
    get usageHeading(): string {
        return this.isLiveAgent
            ? 'AutomationAgents.liveAgent.quotaUsage.estimatedMonthly'
            : 'AutomationAgents.adHoc.quotaUsage.mentions';
    }

    /** Nothing is estimated until the picker has resolved a source and its volume has been counted */
    get hasEstimate(): boolean {
        return this.mentionCount != null;
    }

    /** Mentions already charged this period */
    get quotaUsed(): number {
        return this.quota?.count ?? 0;
    }

    /** What the pool allows this period - monthly limit plus any top up */
    get quotaTotal(): number {
        return this.quota?.totalLimit ?? 0;
    }

    /**
     * What this would add to it: the mentions an ad hoc run covers, or, for a live agent, the
     * sample window's volume projected across a month the way the curation panel projects it.
     */
    get estimatedUsage(): number {
        if (this.mentionCount == null) {
            return 0;
        }

        return this.isLiveAgent
            ? Math.round(this.mentionCount / quotaSampleDays * quotaProjectionDays)
            : this.mentionCount;
    }

    /**
     * Where the account lands by the end of the period if it carries on at the rate it has been
     * going, beyond what is already spent and what this run adds - the bar stacks the three.
     *
     * Derived from the pool's own window rather than reported by the API. A rate taken over a few
     * hours says nothing, and scaled up to a month it swamps the bar, so there is no projection
     * until a full day of the period has passed.
     */
    get overallEstimate(): number {
        const start = this.quota?.windowStart;
        const reset = this.quota?.nextResetDate;
        const elapsed = start ? Date.now() - start : 0;

        if (!start || !reset || elapsed < this.dayMs) {
            return 0;
        }

        const period = Math.max(reset - start, elapsed);
        const projected = Math.round(this.quotaUsed * (period / elapsed));

        return Math.max(0, Math.min(this.quotaTotal, projected) - this.quotaUsed - this.estimatedUsage);
    }

    get quotaRemaining(): number {
        return Math.max(0, this.quotaTotal - this.quotaUsed - this.estimatedUsage - this.overallEstimate);
    }

    /** Whether the run would take the account past its pool, which is worth saying before it runs */
    get exceedsQuota(): boolean {
        return this.quotaTotal > 0 && this.quotaUsed + this.estimatedUsage > this.quotaTotal;
    }

    /** Width of one segment of the usage bar. Clamped so a run over quota still fills it exactly */
    quotaPercent(value: number): number {
        if (this.quotaTotal <= 0) {
            return 0;
        }

        return Math.min(100, (value / this.quotaTotal) * 100);
    }

    /**
     * What hovering a segment reports: its label, a rule, then its swatch and figure - the card the
     * design shows. Styled inline because the directive writes this straight into the tooltip,
     * which is rendered outside this component's styles.
     */
    quotaTooltip(label: string, value: number, color: string): string {
        const share = this.quotaTotal > 0 ? ` (${Math.round(this.quotaPercent(value))}%)` : '';
        const swatch = 'display:inline-block;width:12px;height:12px;border-radius:3px;'
            + `background:${color};vertical-align:middle;margin-right:8px;`;

        return `<strong>${this.localizationService.transform(label)}</strong>`
            + `<hr style="border:none;border-top:1px solid ${this.quotaColors.track};margin:6px 0;">`
            + `<span style="${swatch}"></span>`
            + `<span style="vertical-align:middle;">${value.toLocaleString()}${share}</span>`;
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.subscriptions.length = 0;
    }

    get canRun(): boolean {
        // A live agent is worth putting on a source that is quiet today, so it only needs a source
        const hasCoverage = this.isLiveAgent ? this.source.isComplete() : !!this.mentionCount;

        return !!this.selectedAgentId && hasCoverage && !this.running && !this.isSelectedAgentOnSource;
    }

    /** A live agent already watching this source would only be put on it a second time */
    private get isSelectedAgentOnSource(): boolean {
        return this.isLiveAgent && this.isOnSource(this.agents.find(agent => agent.id === this.selectedAgentId));
    }

    private isOnSource(agent: CurationAgentModel): boolean {
        const dataSource = this.source.dataSource;

        return !!dataSource?.id && (agent?.sources ?? []).some(source => source.dataSource?.type === dataSource.type
            && Number(source.dataSource?.id) === Number(dataSource.id));
    }

    /** Every agent, less - for a live agent - the ones already on the picked source */
    private setAgentOptions(): void {
        const agents = this.isLiveAgent ? this.agents.filter(agent => !this.isOnSource(agent)) : this.agents;

        this.agentOptions = [
            new DropdownOptionModel('AutomationAgents.adHoc.agent.new', this.newAgentOptionValue),
            ...agents.map(agent => new DropdownOptionModel(agent.name, agent.id))
        ];

        if (this.isSelectedAgentOnSource) {
            this.selectedAgentId = null;
        }
    }

    /** The footer button, which either starts a live agent or submits the ad hoc run */
    submit(): void {
        if (this.isLiveAgent) {
            this.startLiveAgent();

            return;
        }

        this.run();
    }

    /**
     * Puts the chosen agent on this source for good. Saving the agent rather than posting the source
     * on its own is deliberate: the agent's save reconciles sources and writes the row's filters into
     * a saved search first, which a bare create would leave behind.
     */
    private startLiveAgent(): void {
        if (!this.canRun) {
            return;
        }

        this.running = true;

        // The listed agent carries no actions or source filters, and saving it as it is would clear
        // them, so the agent is reloaded in full before the source is added to it
        this.curationAgentsService.get(this.selectedAgentId).pipe(
            switchMap(agent => {
                agent.sources.push(this.source);

                return this.curationAgentsService.save(agent);
            }),
            take(1)
        ).subscribe({
            next: () => {
                this.running = false;
                this.cancel();
            },
            // The service growls for itself
            error: () => this.running = false
        });
    }

    sourceChanged(): void {
        this.setAgentOptions();
        this.recount();
    }

    dateRangeChanged(dateRange: DateQueryModel): void {
        this.dateRange = dateRange;
        this.recount();
    }

    /** Hands the chosen coverage to the agent as a query, which the worker resolves into mentions */
    run(): void {
        if (!this.canRun) {
            return;
        }

        if (!this.source.isComplete()) {
            return;
        }

        this.running = true;
        // Submitted as the source it was picked from, not as a bare query, so the run records what
        // it ran over - the ad hoc history names the source rather than "Selected Coverage"
        this.curationAgentsService.saveAdHocFilter(this.source, this.dateRange).pipe(
            switchMap(filterSearchCriteriaId => this.curationAgentsApi.submitMentions(this.selectedAgentId, {
                sourceType: this.source.dataSource.type,
                sourceRef: String(this.source.dataSource.id),
                filterSearchCriteriaId: filterSearchCriteriaId
            })),
            take(1)
        ).subscribe({
            next: () => {
                this.running = false;
                // Puts the new run in front of the dashboard's Ad Hoc History and the sidebar's
                // event list, both of which read the shared refresh
                this.curationAgentsService.refresh();
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform('AutomationAgents.adHoc.growl.submitted.summary'),
                    detail: this.localizationService.transform('AutomationAgents.adHoc.growl.submitted.detail')
                });
                this.cancel();
            },
            error: (error: HttpErrorResponse) => {
                this.running = false;

                // The service refuses a run while the account is over quota, which is worth saying
                // plainly rather than as a failure the user could retry
                const overQuota = error?.status === this.conflictStatus;

                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform(overQuota
                        ? 'AutomationAgents.adHoc.growl.overQuota.summary'
                        : 'AutomationAgents.adHoc.growl.failed.summary'),
                    detail: this.localizationService.transform(overQuota
                        ? 'AutomationAgents.adHoc.growl.overQuota.detail'
                        : 'AutomationAgents.adHoc.growl.failed.detail')
                });
            }
        });
    }

    /** Picking "Create New Agent" opens the create panel and leaves the dropdown on what it was before */
    agentSelected(agentId: number): void {
        if (agentId !== this.newAgentOptionValue) {
            this.selectedAgentId = agentId;

            return;
        }

        const previous = this.selectedAgentId;

        // Taken once the dropdown has written the sentinel, otherwise the revert is not seen as a change
        this.selectedAgentId = agentId;
        setTimeout(() => this.selectedAgentId = previous);

        this.openCreateAgentPanel();
    }

    private openCreateAgentPanel(): void {
        const knownIds = new Set(this.agents.map(agent => agent.id));

        this.coreDialogService.open(CurationAgentPanelComponent, {
            inputs: {
                animationTrigger: new BehaviorSubject(new FullProfileAnimationModel(
                    FullProfileDialogAnimationsEnum.slideInOutAnimation,
                    OverlayAnimationStateEnum.open
                )),
                overlayContainer: this.overlayContainer
            },
            outputs: {
                // The panel hands back the agent only when it was saved
                dialogClose: (saved: CurationAgentModel) => {
                    if (saved) {
                        this.agentIdsBeforeCreate = knownIds;
                        this.selectCreatedAgent();
                    }
                }
            }
        });
    }

    /** Selects the agent the create panel saved, once the refreshed list brings it in */
    private selectCreatedAgent(): void {
        const created = this.agentIdsBeforeCreate
            && this.agents.find(agent => !this.agentIdsBeforeCreate.has(agent.id));

        if (created) {
            this.selectedAgentId = created.id;
            this.agentIdsBeforeCreate = null;
        }
    }

    /**
     * Cancel runs the panel's own slide-out so it matches the X and click-outside; the dialog itself
     * is torn down by {@link onVisibleChange} once that animation finishes.
     */
    cancel(): void {
        if (this.takeOverPanel?.isPanelFullyOpened) {
            this.takeOverPanel.close();

            return;
        }

        this.closeDialog();
    }

    onVisibleChange(isVisible: boolean): void {
        if (!isVisible) {
            this.closeDialog();
        }
    }

    private recount(): void {
        if (!this.source.isComplete()) {
            this.mentionCount = null;
            this.countingMentions = false;

            return;
        }

        this.countingMentions = true;
        this.countRequests.next();
    }

    private buildCriteria(): MentionSearchCriteriaModel {
        const dataSource = this.source.dataSource;
        const criteria = MentionSearchCriteriaModel.createFiltered(
            dataSource?.sourceSearchCriteria,
            dataSource?.filterSearchCriteria
        );

        if (!criteria) {
            return null;
        }

        criteria.query.dateQuery = this.isLiveAgent
            ? DateQueryModel.getLastXDaysQuery(quotaSampleDays)
            : this.dateRange;

        return criteria;
    }

    /** How many mentions the chosen coverage covers. Null on failure, so the banner says so. */
    private countMentions(): Observable<number> {
        const criteria = this.buildCriteria();

        if (!criteria) {
            return of(null);
        }

        // Only the total is needed, so ask for as little as the endpoint allows
        criteria.pageSize = 1;
        criteria.facets = [];
        criteria.dateFacets = [];

        const apiEntity = criteria.getApiEntity();
        const search = this.source.dataSource.isSavedSource()
            ? this.mentionsApi.searchAccount(apiEntity)
            : this.mentionsApi.searchMentions(apiEntity);

        return search.pipe(
            map((response: MentionsSearchResponseModel) => response?.totalResults ?? 0),
            catchError(() => of(null))
        );
    }
}
